import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  KeyIcon,
  PlusIcon,
  TrashIcon,
  ClipboardDocumentIcon,
  XMarkIcon,
  LockClosedIcon,
  EyeIcon,
  PencilSquareIcon,
  MagnifyingGlassIcon,
  BookOpenIcon,
} from '@heroicons/react/24/outline';
import toast from 'react-hot-toast';
import { useApiKeys, useApiScopes, useCreateApiKey, useRevokeApiKey, useUpdateApiKeyScopes, useQuotaStatus } from '@/hooks/useApi';
import { getApiErrorCode } from '@/lib/errorCodes';
import type { ApiKey, ApiScope, CreateApiKeyPayload } from '@/types';
import PageSkeleton from '@/components/PageSkeleton';

export default function ApiKeysPage() {
  const { t } = useTranslation();
  const { plan, planLabel, isLoaded } = useQuotaStatus();
  const eligible = plan === 'professional' || plan === 'enterprise';

  const { data: keysResponse, isLoading } = useApiKeys();
  const { data: scopes } = useApiScopes();
  const createKey = useCreateApiKey();
  const revokeKey = useRevokeApiKey();
  const updateScopes = useUpdateApiKeyScopes();

  const [showCreate, setShowCreate] = useState(false);
  const [createdKey, setCreatedKey] = useState<ApiKey | null>(null);
  const [scopesModal, setScopesModal] = useState<{ key: ApiKey; mode: 'view' | 'edit' } | null>(null);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState<'all' | 'active' | 'revoked' | 'expired'>('all');

  if (!isLoaded) return <PageSkeleton variant="detail" />;

  // Plan gating
  if (!eligible) {
    return (
      <div>
        <h1 className="text-2xl font-bold mb-6 flex items-center gap-2">
          <KeyIcon className="w-6 h-6" /> {t('apiKeys.title')}
        </h1>
        <div className="card card-body max-w-2xl text-center py-12">
          <div className="mx-auto w-14 h-14 rounded-full bg-violet-100 flex items-center justify-center mb-4">
            <LockClosedIcon className="w-7 h-7 text-violet-600" />
          </div>
          <h2 className="text-xl font-semibold mb-2">{t('apiKeys.upgradeTitle')}</h2>
          <p className="text-gray-600 mb-2">
            {t('apiKeys.upgradeDescription')}
          </p>
          <p className="text-sm text-gray-500 mb-6">
            {t('apiKeys.currentPlan')}: <span className="font-medium">{planLabel}</span>
          </p>
          <Link to="/subscription" className="btn btn-primary mx-auto">
            {t('apiKeys.upgradeCta')}
          </Link>
        </div>
      </div>
    );
  }

  const allKeys = keysResponse ?? [];

  const keys = allKeys.filter((k) => {
    if (statusFilter === 'active' && !k.isActive) return false;
    if (statusFilter === 'revoked' && !k.isRevoked) return false;
    if (statusFilter === 'expired' && (!k.isExpired || k.isRevoked)) return false;
    const q = search.trim().toLowerCase();
    if (q && !k.name.toLowerCase().includes(q) && !k.tokenPrefix.toLowerCase().includes(q)) return false;
    return true;
  });

  return (
    <div>
      {/* Header */}
      <div className="flex items-start justify-between mb-6 flex-wrap gap-3">
        <div>
          <h1 className="text-2xl font-bold flex items-center gap-2">
            <KeyIcon className="w-6 h-6" /> {t('apiKeys.title')}
          </h1>
          <p className="text-sm text-gray-500 mt-1">{t('apiKeys.subtitle')}</p>
        </div>
        <div className="flex gap-2">
          <Link
            to="/settings/api-keys/docs"
            className="btn btn-secondary inline-flex items-center gap-2"
          >
            <BookOpenIcon className="w-4 h-4" />
            {t('apiKeys.viewDocs')}
          </Link>
          <button onClick={() => setShowCreate(true)} className="btn btn-primary inline-flex items-center gap-2">
            <PlusIcon className="w-4 h-4" /> {t('apiKeys.create')}
          </button>
        </div>
      </div>

      {/* Toolbar */}
      {allKeys.length > 0 && (
        <div className="flex flex-wrap items-center gap-2 mb-4">
          <div className="relative flex-1 min-w-[220px]">
            <MagnifyingGlassIcon className="w-4 h-4 text-gray-400 absolute left-3 top-1/2 -translate-y-1/2 pointer-events-none" />
            <input
              type="text"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder={t('apiKeys.searchPlaceholder')}
              className="input pl-9"
            />
          </div>
          <div className="flex bg-gray-100 rounded-lg p-0.5">
            {(['all', 'active', 'expired', 'revoked'] as const).map((s) => (
              <button
                key={s}
                onClick={() => setStatusFilter(s)}
                className={`px-3 py-1.5 text-xs font-medium rounded-md transition-colors ${
                  statusFilter === s ? 'bg-white text-gray-900 shadow-sm' : 'text-gray-600 hover:text-gray-900'
                }`}
              >
                {t(`apiKeys.filter.${s}`)}
              </button>
            ))}
          </div>
        </div>
      )}

      {/* List */}
      {isLoading ? (
        <PageSkeleton variant="list" />
      ) : keys.length === 0 ? (
        <div className="card card-body text-center py-12">
          <KeyIcon className="w-10 h-10 mx-auto text-gray-300 mb-3" />
          <p className="text-gray-500">
            {allKeys.length === 0 ? t('apiKeys.emptyState') : t('apiKeys.noResults')}
          </p>
        </div>
      ) : (
        <div className="card divide-y divide-gray-100">
          {keys.map((k) => (
            <KeyRow
              key={k.id}
              apiKey={k}
              onRevoke={() => revokeKey.mutate(k.id)}
              onViewScopes={() => setScopesModal({ key: k, mode: 'view' })}
              onEditScopes={() => setScopesModal({ key: k, mode: 'edit' })}
            />
          ))}
        </div>
      )}

      {showCreate && (
        <CreateModal
          scopes={scopes ?? []}
          onClose={() => setShowCreate(false)}
          submitting={createKey.isPending}
          onSubmit={async (payload) => {
            const created = await createKey.mutateAsync(payload);
            setShowCreate(false);
            setCreatedKey(created);
          }}
        />
      )}

      {scopesModal && (
        <ScopesModal
          apiKey={scopesModal.key}
          allScopes={scopes ?? []}
          mode={scopesModal.mode}
          onClose={() => setScopesModal(null)}
          submitting={updateScopes.isPending}
          onSave={async (next) => {
            await updateScopes.mutateAsync({ id: scopesModal.key.id, scopes: next });
            toast.success(t('apiKeys.scopesUpdated'));
            setScopesModal(null);
          }}
        />
      )}

      {createdKey && (
        <TokenRevealModal apiKey={createdKey} onClose={() => setCreatedKey(null)} />
      )}
    </div>
  );
}

// ────────────────────────────────────────────────────────────────────────────

function TokenRevealModal({ apiKey, onClose }: { apiKey: ApiKey; onClose: () => void }) {
  const { t } = useTranslation();

  async function copyToken() {
    if (!apiKey.token) return;
    await navigator.clipboard.writeText(apiKey.token);
    toast.success(t('apiKeys.copied'));
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
      <div className="bg-white rounded-xl shadow-xl w-full max-w-lg overflow-hidden">
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200">
          <h3 className="text-lg font-semibold">{t('apiKeys.tokenRevealTitle')}</h3>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600">
            <XMarkIcon className="w-5 h-5" />
          </button>
        </div>
        <div className="px-6 py-5 space-y-4">
          <p className="text-sm text-amber-700 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2">
            {t('apiKeys.tokenRevealWarning')}
          </p>
          <div className="flex items-center gap-2">
            <code className="flex-1 font-mono text-xs bg-gray-50 border border-gray-200 rounded-lg px-3 py-2 break-all">
              {apiKey.token}
            </code>
            <button
              type="button"
              onClick={copyToken}
              className="p-2 rounded-md text-gray-500 hover:text-gray-800 hover:bg-gray-100 shrink-0"
              title={t('common.copy')}
            >
              <ClipboardDocumentIcon className="w-5 h-5" />
            </button>
          </div>
        </div>
        <div className="flex justify-end gap-2 px-6 py-4 border-t border-gray-200 bg-gray-50">
          <button type="button" onClick={onClose} className="btn btn-primary">{t('common.done')}</button>
        </div>
      </div>
    </div>
  );
}

function KeyRow({
  apiKey,
  onRevoke,
  onViewScopes,
  onEditScopes,
}: {
  apiKey: ApiKey;
  onRevoke: () => void;
  onViewScopes: () => void;
  onEditScopes: () => void;
}) {
  const { t } = useTranslation();
  const [confirming, setConfirming] = useState(false);

  const status = apiKey.isRevoked
    ? { label: t('apiKeys.statusRevoked'), badge: 'bg-gray-100 text-gray-600', dot: 'bg-gray-400' }
    : apiKey.isExpired
    ? { label: t('apiKeys.statusExpired'), badge: 'bg-amber-50 text-amber-700', dot: 'bg-amber-500' }
    : { label: t('apiKeys.statusActive'), badge: 'bg-emerald-50 text-emerald-700', dot: 'bg-emerald-500' };

  async function copyToken() {
    if (!apiKey.token) {
      toast.error(t('apiKeys.tokenUnavailable'));
      return;
    }
    await navigator.clipboard.writeText(apiKey.token);
    toast.success(t('apiKeys.copied'));
  }

  const lastUsed = apiKey.lastUsedAt
    ? new Date(apiKey.lastUsedAt).toLocaleDateString()
    : t('apiKeys.neverUsed');

  return (
    <div className={`flex items-center gap-3 px-4 py-3 hover:bg-gray-50 transition-colors ${apiKey.isRevoked ? 'opacity-60' : ''}`}>
      {/* Left: identity */}
      <div className="min-w-0 flex-1 flex items-center gap-3">
        <span className={`w-1.5 h-1.5 rounded-full shrink-0 ${status.dot}`} />
        <div className="min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <span className="font-medium text-gray-900 truncate">{apiKey.name}</span>
            <code className="font-mono text-[11px] text-gray-500 truncate">{apiKey.tokenPrefix}…</code>
          </div>
          <div className="text-xs text-gray-500 mt-0.5 truncate">
            {lastUsed} · {apiKey.scopes.length} {t('apiKeys.scopes').toLowerCase()}
          </div>
        </div>
      </div>

      {/* Right: status + actions */}
      <div className="flex items-center gap-1 shrink-0">
        <span className={`hidden sm:inline-flex px-2 py-0.5 rounded-full text-[11px] font-medium ${status.badge}`}>
          {status.label}
        </span>
        <button
          type="button"
          onClick={copyToken}
          className="p-1.5 rounded-md text-gray-400 hover:text-gray-700 hover:bg-gray-100"
          title={t('common.copy')}
        >
          <ClipboardDocumentIcon className="w-4 h-4" />
        </button>
        <button
          type="button"
          onClick={onViewScopes}
          className="p-1.5 rounded-md text-gray-400 hover:text-gray-700 hover:bg-gray-100"
          title={t('apiKeys.viewScopesTitle')}
        >
          <EyeIcon className="w-4 h-4" />
        </button>
        {!apiKey.isRevoked && (
          <button
            type="button"
            onClick={onEditScopes}
            className="p-1.5 rounded-md text-gray-400 hover:text-gray-700 hover:bg-gray-100"
            title={t('apiKeys.editScopes')}
          >
            <PencilSquareIcon className="w-4 h-4" />
          </button>
        )}
        {!apiKey.isRevoked && (
          confirming ? (
            <div className="flex items-center gap-1 ml-1">
              <button onClick={onRevoke} className="px-2 py-1 text-xs rounded bg-red-600 text-white hover:bg-red-700">
                {t('common.confirm')}
              </button>
              <button onClick={() => setConfirming(false)} className="px-2 py-1 text-xs rounded border border-gray-300">
                {t('common.cancel')}
              </button>
            </div>
          ) : (
            <button
              type="button"
              onClick={() => setConfirming(true)}
              className="p-1.5 rounded-md text-gray-400 hover:text-red-600 hover:bg-red-50"
              title={t('apiKeys.revoke')}
            >
              <TrashIcon className="w-4 h-4" />
            </button>
          )
        )}
      </div>
    </div>
  );
}

// ────────────────────────────────────────────────────────────────────────────

function CreateModal({
  scopes,
  onClose,
  onSubmit,
  submitting,
}: {
  scopes: ApiScope[];
  onClose: () => void;
  onSubmit: (payload: CreateApiKeyPayload) => void | Promise<void>;
  submitting: boolean;
}) {
  const { t } = useTranslation();
  const [name, setName] = useState('');
  const [selectedScopes, setSelectedScopes] = useState<string[]>(() => scopes.map((s) => s.value));
  const [ips, setIps] = useState('');
  const [expiresAt, setExpiresAt] = useState('');

  const grouped = scopes.reduce<Record<string, ApiScope[]>>((acc, s) => {
    (acc[s.resource] ||= []).push(s);
    return acc;
  }, {});

  function toggleScope(value: string) {
    setSelectedScopes((cur) => (cur.includes(value) ? cur.filter((v) => v !== value) : [...cur, value]));
  }

  function handleSubmit(e?: React.FormEvent) {
    e?.preventDefault();
    if (!name.trim() || selectedScopes.length === 0) {
      toast.error(t('apiKeys.validationError'));
      return;
    }
    const ipList = ips
      .split('\n')
      .map((s) => s.trim())
      .filter(Boolean);
    Promise.resolve(
      onSubmit({
        name: name.trim(),
        scopes: selectedScopes,
        ipAllowlist: ipList.length ? ipList : null,
        expiresAt: expiresAt || null,
      })
    ).catch((error) => {
      const errorCode = getApiErrorCode(error);
      if (errorCode) {
        const localized = t(errorCode);
        if (localized !== errorCode) {
          toast.error(localized);
          return;
        }
      }
      toast.error(t('common.error'));
    });
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
      <div className="bg-white rounded-xl shadow-xl w-full max-w-2xl max-h-[90vh] overflow-hidden flex flex-col">
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200">
          <h3 className="text-lg font-semibold">{t('apiKeys.createTitle')}</h3>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600">
            <XMarkIcon className="w-5 h-5" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="flex-1 overflow-y-auto px-6 py-4 space-y-5">
          <div>
            <label className="label">{t('apiKeys.name')} *</label>
            <input
              type="text"
              className="input"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder={t('apiKeys.namePlaceholder')}
              required
              maxLength={120}
            />
          </div>

          <div>
            <label className="label">{t('apiKeys.scopes')} *</label>
            <p className="text-xs text-gray-500 mb-2">{t('apiKeys.scopesHint')}</p>
            <div className="space-y-3">
              {Object.entries(grouped).map(([resource, list]) => (
                <div key={resource} className="border border-gray-200 rounded-lg p-3">
                  <div className="text-xs font-semibold uppercase tracking-wide text-gray-500 mb-2">{resource}</div>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                    {list.map((s) => (
                      <label key={s.value} className="flex items-start gap-2 text-sm cursor-pointer">
                        <input
                          type="checkbox"
                          checked={selectedScopes.includes(s.value)}
                          onChange={() => toggleScope(s.value)}
                          className="mt-0.5"
                        />
                        <span>
                          <span className="font-mono text-xs text-violet-700">{s.value}</span>
                          <span className="block text-xs text-gray-500">{s.label}</span>
                        </span>
                      </label>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="label">{t('apiKeys.expiresAt')}</label>
              <input
                type="date"
                className="input"
                value={expiresAt}
                onChange={(e) => setExpiresAt(e.target.value)}
                min={new Date(Date.now() + 86400000).toISOString().split('T')[0]}
              />
              <p className="text-xs text-gray-500 mt-1">{t('apiKeys.expiresHint')}</p>
            </div>
            <div>
              <label className="label">{t('apiKeys.ipAllowlist')}</label>
              <textarea
                className="input font-mono text-xs"
                rows={3}
                value={ips}
                onChange={(e) => setIps(e.target.value)}
                placeholder={'203.0.113.5\n10.0.0.0/24'}
              />
              <p className="text-xs text-gray-500 mt-1">{t('apiKeys.ipHint')}</p>
            </div>
          </div>
        </form>

        <div className="flex justify-end gap-2 px-6 py-4 border-t border-gray-200 bg-gray-50">
          <button type="button" onClick={onClose} className="btn btn-secondary">{t('common.cancel')}</button>
          <button type="button" onClick={() => handleSubmit()} disabled={submitting} className="btn btn-primary">
            {submitting ? t('common.saving') : t('apiKeys.create')}
          </button>
        </div>
      </div>
    </div>
  );
}

// ────────────────────────────────────────────────────────────────────────────

function ScopesModal({
  apiKey,
  allScopes,
  mode,
  onClose,
  onSave,
  submitting,
}: {
  apiKey: ApiKey;
  allScopes: ApiScope[];
  mode: 'view' | 'edit';
  onClose: () => void;
  onSave: (scopes: string[]) => void | Promise<void>;
  submitting: boolean;
}) {
  const { t } = useTranslation();
  const [selected, setSelected] = useState<string[]>(apiKey.scopes);

  const grouped = allScopes.reduce<Record<string, ApiScope[]>>((acc, s) => {
    (acc[s.resource] ||= []).push(s);
    return acc;
  }, {});

  function toggle(value: string) {
    setSelected((cur) => (cur.includes(value) ? cur.filter((v) => v !== value) : [...cur, value]));
  }

  function handleSave() {
    if (selected.length === 0) {
      toast.error(t('apiKeys.validationError'));
      return;
    }
    void onSave(selected);
  }

  const isEdit = mode === 'edit';

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
      <div className="bg-white rounded-xl shadow-xl w-full max-w-2xl max-h-[90vh] overflow-hidden flex flex-col">
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200">
          <div>
            <h3 className="text-lg font-semibold">
              {isEdit ? t('apiKeys.editScopesTitle') : t('apiKeys.viewScopesTitle')}
            </h3>
            <p className="text-xs text-gray-500 mt-0.5">{apiKey.name}</p>
          </div>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600">
            <XMarkIcon className="w-5 h-5" />
          </button>
        </div>

        <div className="flex-1 overflow-y-auto px-6 py-4 space-y-3">
          {Object.entries(grouped).map(([resource, list]) => (
            <div key={resource} className="border border-gray-200 rounded-lg p-3">
              <div className="text-xs font-semibold uppercase tracking-wide text-gray-500 mb-2">{resource}</div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                {list.map((s) => {
                  const checked = selected.includes(s.value);
                  return (
                    <label
                      key={s.value}
                      className={`flex items-start gap-2 text-sm ${isEdit ? 'cursor-pointer' : 'cursor-default'}`}
                    >
                      <input
                        type="checkbox"
                        checked={checked}
                        disabled={!isEdit}
                        onChange={() => toggle(s.value)}
                        className="mt-0.5"
                      />
                      <span>
                        <span className="font-mono text-xs text-violet-700">{s.value}</span>
                        <span className="block text-xs text-gray-500">{s.label}</span>
                      </span>
                    </label>
                  );
                })}
              </div>
            </div>
          ))}
        </div>

        <div className="flex justify-end gap-2 px-6 py-4 border-t border-gray-200 bg-gray-50">
          <button type="button" onClick={onClose} className="btn btn-secondary">
            {isEdit ? t('common.cancel') : t('common.close')}
          </button>
          {isEdit && (
            <button type="button" onClick={handleSave} disabled={submitting} className="btn btn-primary">
              {submitting ? t('common.saving') : t('common.save')}
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

